import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { HsnSacField } from '@/features/common/HsnSacField';
import { Button, Sheet, SheetBody, SheetFooter, SheetHeader } from '@/features/common/ui';
import { suggestSac } from '@fint/validation';

/**
 * The items with no HSN/SAC, each with a suggested code, confirmed in one press.
 *
 * ── WHY A SHEET AND NOT THIRTEEN EDITS ──
 * The migration brought the catalogue in without codes, because the old
 * system printed none. The Review says so, one finding per item; fixing it
 * through the item form is thirteen open-type-save trips for what a CA does
 * in a minute with the list in front of them. This is that minute: every
 * item that has no code, the heading its name suggests already in the box
 * with its wording, the ones the name does not place left empty for the
 * person to fill or skip.
 *
 * ── THE LINES ──
 * A code on the item reaches the NEXT invoice only. The sixteen invoices
 * already issued snapshotted an empty code, and GSTR-1 Table 12 reads the
 * lines - so the box below offers to write the code onto every line drawn
 * from the item that has none. It moves no figure; it is offered only to a
 * person who may restate an issued invoice, and it is audited as its own
 * act. A line that already carries a code is never touched.
 */

interface Gap {
  id: string;
  name: string;
  type: 'goods' | 'service';
  lines: number;
}

interface Gaps {
  items: Gap[];
  adhocLines: number;
  canFillLines: boolean;
}

export function ApplyHsnSheet({ onClose }: { onClose: () => void }) {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [fillLines, setFillLines] = useState(true);
  const [result, setResult] = useState<{ itemsUpdated: number; linesFilled: number } | null>(null);

  const gaps = useQuery({
    queryKey: ['items', org?.id, 'hsn-gaps'],
    queryFn: () => api.get<Gaps>('/items/hsn-gaps'),
    enabled: !!org?.id,
  });

  // The suggestions, once, when the list arrives - the person edits from there.
  useEffect(() => {
    if (!gaps.data) return;
    setCodes((current) => {
      if (Object.keys(current).length) return current;
      const next: Record<string, string> = {};
      // Only a service is guessed at; an HSN for goods is the person's to type.
      for (const item of gaps.data.items) next[item.id] = item.type === 'service' ? (suggestSac(item.name)?.code ?? '') : '';
      return next;
    });
  }, [gaps.data]);

  const items = gaps.data?.items ?? [];
  const assignments = items
    .map((item) => ({ itemId: item.id, hsnSac: (codes[item.id] ?? '').trim() }))
    .filter((a) => a.hsnSac.length >= 4);
  const linesToFill = items.filter((item) => (codes[item.id] ?? '').trim().length >= 4).reduce((sum, item) => sum + item.lines, 0);
  const canFill = !!gaps.data?.canFillLines;

  const apply = useMutation({
    mutationFn: () => api.post<{ itemsUpdated: number; linesFilled: number }>('/items/apply-hsn', { assignments, fillLines: canFill && fillLines }),
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ['items', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['gst', org?.id] });
    },
  });
  const error = apply.error instanceof ApiError ? apply.error : undefined;

  const titleId = 'apply-hsn-title';

  return (
    <Sheet labelledBy={titleId} onClose={onClose} wide testId="apply-hsn">
      <SheetHeader title="Apply HSN/SAC codes" titleId={titleId} onClose={onClose} />
      <SheetBody>
        {result ? (
          <div className="rounded-md border border-border bg-muted/40 p-4 text-sm" role="status" data-testid="apply-hsn-result">
            <p className="font-medium">
              {result.itemsUpdated === 1 ? '1 item' : `${result.itemsUpdated} items`} now carry a code
              {result.linesFilled ? `, and ${result.linesFilled === 1 ? '1 invoice line' : `${result.linesFilled} invoice lines`} that had none were filled` : ''}.
            </p>
            <p className="mt-1 text-muted-foreground">GSTR-1 Table 12 reads the lines, so the return for those months now accounts for them.</p>
          </div>
        ) : gaps.isLoading ? (
          <p className="text-sm text-muted-foreground">Looking for items without a code…</p>
        ) : gaps.isError ? (
          <p className="text-sm text-destructive">The list could not be loaded.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="apply-hsn-empty">
            Every active item has a code.
            {gaps.data?.adhocLines ? ` ${gaps.data.adhocLines} lines typed by hand on invoices have none - those are fixed on their invoices.` : ''}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Every GST invoice line needs an HSN (goods) or SAC (services). The heading each name suggests is filled in with its
              wording - check it, change it, or leave one empty to skip it. For goods, type the HSN from your supplier&rsquo;s invoice or
              your CA.
            </p>
            <ul className="divide-y divide-border rounded-md border border-border" data-testid="apply-hsn-rows">
              {items.map((item) => (
                <li key={item.id} className="grid gap-2 p-3 sm:grid-cols-[1fr_minmax(0,16rem)] sm:items-start" data-testid="apply-hsn-row">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.type === 'goods' ? 'Goods' : 'Service'}
                      {item.lines ? ` · on ${item.lines === 1 ? '1 invoice line' : `${item.lines} invoice lines`} without a code` : ''}
                    </p>
                  </div>
                  <HsnSacField
                    compact
                    label={`HSN/SAC for ${item.name}`}
                    value={codes[item.id] ?? ''}
                    onChange={(next) => setCodes((c) => ({ ...c, [item.id]: next }))}
                    nameForSuggestion={item.type === 'service' ? item.name : null}
                  />
                </li>
              ))}
            </ul>
            {canFill ? (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4"
                  checked={fillLines}
                  onChange={(e) => setFillLines(e.target.checked)}
                  data-testid="apply-hsn-fill-lines"
                />
                <span>
                  Also fill the code on the {linesToFill === 1 ? '1 invoice line' : `${linesToFill} invoice lines`} drawn from these items that have
                  none. No figure changes; GSTR-1 Table 12 will account for them.
                </span>
              </label>
            ) : (
              <p className="text-xs text-muted-foreground">
                Lines on invoices already issued keep their empty code - filling those needs permission to change issued invoices.
              </p>
            )}
            {gaps.data?.adhocLines ? (
              <p className="text-xs text-muted-foreground">
                {gaps.data.adhocLines === 1 ? '1 line' : `${gaps.data.adhocLines} lines`} typed by hand on invoices have no code either; those are
                fixed on their invoices.
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            ) : null}
          </>
        )}
      </SheetBody>
      <SheetFooter>
        {result ? (
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button type="button" onClick={() => apply.mutate()} disabled={assignments.length === 0} loading={apply.isPending} data-testid="apply-hsn-go">
              {assignments.length === 1 ? 'Apply to 1 item' : `Apply to ${assignments.length} items`}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={apply.isPending}>
              Cancel
            </Button>
          </>
        )}
      </SheetFooter>
    </Sheet>
  );
}
